import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import {
	TenantsSkeleton,
	TenantDetailSkeleton,
	AccessSkeleton,
	AuditSkeleton,
	ReleasesSkeleton,
} from "../components/KccSkeleton";

// Keep the import thunks so we can both lazy-mount and prefetch the same chunk
// (webpack dedupes by module, so a prefetch warms the exact chunk the route uses).
const importTenantDetail = () => import("../pages/TenantDetail");

const Tenants = lazy(() => import("../pages/Tenants"));
const TenantDetail = lazy(importTenantDetail);
const Access = lazy(() => import("../pages/Access"));
const Releases = lazy(() => import("../pages/Releases"));
const Audit = lazy(() => import("../pages/Audit"));

// The tenant-detail chunk is the heaviest (ECharts + live preview). Call this from
// the Tenants list so the chunk downloads in the background and opening a tenant
// is instant instead of waiting on the lazy load.
export const prefetchTenantDetail = importTenantDetail;

// Per-route Suspense so the lazy chunk loads behind the screen's own skeleton —
// navigation feels instant (skeleton shows immediately) instead of blocking on a
// spinner, then the page's data-loading skeleton takes over seamlessly.
const withSkeleton = (node: React.ReactNode, fallback: React.ReactNode) => (
	<Suspense fallback={fallback}>{node}</Suspense>
);

const RoutesConfig: React.FC = () => {
	return (
		<Routes>
			<Route path="/" element={<Navigate to="/tenants" replace />} />
			<Route path="/tenants" element={withSkeleton(<Tenants />, <TenantsSkeleton />)} />
			<Route path="/tenants/:tenantId" element={withSkeleton(<TenantDetail />, <TenantDetailSkeleton />)} />
			<Route path="/access" element={withSkeleton(<Access />, <AccessSkeleton />)} />
			{/* MUST have its own Suspense fallback like every other lazy route:
			    without one, a hard refresh at /releases suspends up the tree and
			    the auth bootstrap's synchronous state update mid-load throws
			    React #426 ("component suspended while responding to synchronous
			    input") — the whole screen crashed to the error boundary. */}
			<Route path="/releases" element={withSkeleton(<Releases />, <ReleasesSkeleton />)} />
			<Route path="/audit" element={withSkeleton(<Audit />, <AuditSkeleton />)} />
			<Route path="*" element={<Navigate to="/tenants" replace />} />
		</Routes>
	);
};

export default RoutesConfig;
